import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
    loadCurrentlyTakingBloodPressureLoweringMedicationTypes,
    loadCurrentlyTakingDiabetesMedicationTypes,
    loadCurrentlyTakingMedicationDetails,
    loadMedicationDiscussedAndConfirmedTypes
} from '../../../store/directusService';
import {
    clearParticipantAuditEventSearchData,
    clearT2DRPersonalInformationData,
    loadParticipantAuditEvent,
    loadT2DRPersonalInformationData,
    updateT2DRPersonalInformationData
} from '../../../store/participantService';
import { getObjFromId } from '../../../utils/directusFunctions';
import { EDIT_ON_CLOSED_ROLES } from '../../../utils/formValidation/commonConstants';
import { NO_CURRENT_PARTICIPANT_ERROR } from '../../../utils/formValidation/commonErrorConstants';
import {
    CURRENTLY_TAKING_BLOOD_PRESSURE_LOWERING_MEDICATION_TYPES_ERROR,
    CURRENTLY_TAKING_DIABETES_MEDICATION_TYPES_ERROR,
    CURRENTLY_TAKING_MEDICATION_DETAILS_ERROR,
    MEDICATION_DISCUSSED_AND_CONFIRMED_TYPES_ERROR
} from '../../../utils/formValidation/loadingErrorMessageConstants';
import {
    ADVISER,
    hasRole,
    LOCAL_ADMIN,
    MANAGER,
    QUALITY,
    SUPERUSER
} from '../../../utils/userRoles';
import AuditT2DRModal from '../../auditing/auditT2DR/AuditT2DRModal';
import Button from '../../formElements/Button';
import DateSelect from '../../formElements/DateSelect';
import SingleSelect from '../../formElements/SingleSelect';
import Switch from '../../formElements/Switch';
import LoadingSpinner from '../../ui/LoadingSpinner';

import { validate } from './validateT2DR';

import form from '../../../commonStyles/formStyles.module.css';
import local from './t2dr.module.css';

const acceptedRoles = [ADVISER, LOCAL_ADMIN, MANAGER, QUALITY, SUPERUSER];
const _UPDATE_MESSAGE = 'Participant T2DR Health Data has been updated';

const T2DR = () => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const initialState = {
        id: '',
        participantId: '',
        dateDiagnosedWithType2Diabetes: '',
        medicationDiscussedAndConfirmedId: '',
        currentlyTakingDiabetesMedicationId: '',
        currentlyTakingBloodPressureLoweringMedicationId: '',
        currentlyTakingMedicationId: ''
    };
    const [newEntry, setNewEntry] = useState(initialState);
    const [initialStateOnEntry, setInitialStateOnEntry] = useState(initialState);
    const [errors, setErrors] = useState({});
    const [isDisabled, setIsDisabled] = useState(true);
    const [isUpdateButtonDisabled, setIsUpdateButtonDisabled] = useState(false);
    const [isAuditable, setIsAuditable] = useState(false);
    const [showAuditModal, setShowAuditModal] = useState(false);
    const [auditLabel, setAuditLabel] = useState('');

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const medicationDiscussedAndConfirmedTypes = useSelector(
        (state) => state.entities.directusService.medicationDiscussedAndConfirmedTypes
    );
    const currentlyTakingBloodPressureLoweringMedicationTypes = useSelector(
        (state) =>
            state.entities.directusService.currentlyTakingBloodPressureLoweringMedicationTypes
    );
    const currentlyTakingDiabetesMedicationTypes = useSelector(
        (state) => state.entities.directusService.currentlyTakingDiabetesMedicationTypes
    );
    const currentlyTakingMedicationDetails = useSelector(
        (state) => state.entities.directusService.currentlyTakingMedicationDetails
    );

    const t2drPersonalInformation = useSelector(
        (state) => state.entities.participantService.t2drPersonalInformationData
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USEEFFECTS
    useEffect(() => {
        if (!medicationDiscussedAndConfirmedTypes?.length)
            dispatch(loadMedicationDiscussedAndConfirmedTypes());
        if (!currentlyTakingBloodPressureLoweringMedicationTypes?.length)
            dispatch(loadCurrentlyTakingBloodPressureLoweringMedicationTypes());
        if (!currentlyTakingDiabetesMedicationTypes?.length)
            dispatch(loadCurrentlyTakingDiabetesMedicationTypes());
        if (!currentlyTakingMedicationDetails?.length)
            dispatch(loadCurrentlyTakingMedicationDetails());
        return () => cleanUp();
    }, []);

    useEffect(() => {
        if ('id' in currentParticipant)
            dispatch(loadT2DRPersonalInformationData(currentParticipant.id));
    }, [currentParticipant?.id]);

    useEffect(() => {
        if ('status' in currentParticipant) {
            const disableOnClosedStatus =
                currentParticipant.status === 'Closed' && !hasRole(EDIT_ON_CLOSED_ROLES, roles);
            setIsDisabled(!hasRole(acceptedRoles, roles) || disableOnClosedStatus);
        }
    }, [currentParticipant?.status, roles]);

    useEffect(() => {
        if (t2drPersonalInformation?.participantId !== currentParticipant?.id) return;
        if (Object.keys(t2drPersonalInformation)?.length) {
            setNewEntry({ ...t2drPersonalInformation });
            setInitialStateOnEntry({ ...t2drPersonalInformation });
        }
    }, [t2drPersonalInformation, currentParticipant?.id]);

    useEffect(() => {
        setIsUpdateButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    useEffect(() => {
        if (successMessage === _UPDATE_MESSAGE) {
            setIsUpdateButtonDisabled(false);
            setNewEntry({ ...t2drPersonalInformation });
            setInitialStateOnEntry({ ...t2drPersonalInformation });
        }
    }, [successMessage]);

    // HELPER FNS
    const loadingError = () => {
        if (Object.keys(currentParticipant)?.length < 1) return NO_CURRENT_PARTICIPANT_ERROR;
        if (!medicationDiscussedAndConfirmedTypes?.length)
            return MEDICATION_DISCUSSED_AND_CONFIRMED_TYPES_ERROR;
        if (!currentlyTakingBloodPressureLoweringMedicationTypes?.length)
            return CURRENTLY_TAKING_BLOOD_PRESSURE_LOWERING_MEDICATION_TYPES_ERROR;
        if (!currentlyTakingDiabetesMedicationTypes?.length)
            return CURRENTLY_TAKING_DIABETES_MEDICATION_TYPES_ERROR;
        if (!currentlyTakingMedicationDetails?.length)
            return CURRENTLY_TAKING_MEDICATION_DETAILS_ERROR;
    };

    const clearError = (key) => {
        if (key in errors && errors[key].error)
            setErrors((prev) => ({ ...prev, [key]: { error: false } }));
    };

    const cleanUp = () => dispatch(clearT2DRPersonalInformationData());

    // EVENT HANDLERS
    const onUpdate = (key, value) => {
        clearError(key);
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onSearchAuditItems = (field) => {
        if (field && newEntry?.id) {
            dispatch(
                loadParticipantAuditEvent('t2dr-personal-information-data', newEntry.id, field)
            );
            setShowAuditModal(true);
            setAuditLabel(field);
        }
    };

    const onAuditModalClose = (e, reason) => {
        e.stopPropagation();
        if (reason === 'backdropClick') return;
        setShowAuditModal(false);
        setAuditLabel('');
        dispatch(clearParticipantAuditEventSearchData());
    };

    const onSubmit = (e) => {
        e.preventDefault();
        setIsUpdateButtonDisabled(true);
        const newErrors = validate(newEntry, initialStateOnEntry);
        if (Object.values(newErrors).some((el) => el.error)) {
            setErrors(newErrors);
            return;
        }
        const payload = { ...newEntry, participantId: currentParticipant.id };
        dispatch(updateT2DRPersonalInformationData(payload, _UPDATE_MESSAGE));
    };

    // RENDER
    const errorMsg = loadingError();
    if (errorMsg) return <LoadingSpinner content={errorMsg} />;

    return (
        <div>
            <div className={local.headingContent} data-testid="t2dr">
                <Switch
                    id={'auditDisplayFields'}
                    label="Show Audit Fields"
                    disabled={!newEntry.id}
                    onOff={true}
                    customClass="auditSwitch"
                    checked={isAuditable}
                    onChange={(e) => setIsAuditable(e.target.checked)}
                />
            </div>

            <div className={form.formWrapper}>
                <form onSubmit={onSubmit} data-testid="form_start_t2dr" className={form.form}>
                    <div className={form.formSection}>
                        <div className={form.formColumn}>
                            <DateSelect
                                id="dateDiagnosedWithType2Diabetes"
                                label="Date Diagnosed with Type 2 Diabetes"
                                disabled={isDisabled}
                                value={newEntry.dateDiagnosedWithType2Diabetes}
                                error={errors.dateDiagnosedWithType2Diabetes}
                                isAuditable={isAuditable}
                                onSearchAuditItems={onSearchAuditItems}
                                onDateChange={(date) =>
                                    onUpdate('dateDiagnosedWithType2Diabetes', date)
                                }
                            />
                            <SingleSelect
                                id="medicationDiscussedAndConfirmedId"
                                label="Medication Discussed and Confirmed"
                                placeholder="Select medication discussed and confirmed..."
                                disabled={isDisabled}
                                menuItems={medicationDiscussedAndConfirmedTypes || []}
                                selectedId={newEntry.medicationDiscussedAndConfirmedId}
                                selected={getObjFromId(
                                    medicationDiscussedAndConfirmedTypes,
                                    newEntry.medicationDiscussedAndConfirmedId
                                )}
                                isAuditable={isAuditable}
                                onSearchAuditItems={onSearchAuditItems}
                                onChange={(chosenId) =>
                                    onUpdate('medicationDiscussedAndConfirmedId', chosenId)
                                }
                            />
                            <SingleSelect
                                id="currentlyTakingDiabetesMedicationId"
                                label="Currently Taking Diabetes Medication"
                                placeholder="Select currently taking diabetes medication..."
                                disabled={isDisabled}
                                menuItems={currentlyTakingDiabetesMedicationTypes || []}
                                selectedId={newEntry.currentlyTakingDiabetesMedicationId}
                                selected={getObjFromId(
                                    currentlyTakingDiabetesMedicationTypes,
                                    newEntry.currentlyTakingDiabetesMedicationId
                                )}
                                isAuditable={isAuditable}
                                onSearchAuditItems={onSearchAuditItems}
                                onChange={(chosenId) =>
                                    onUpdate('currentlyTakingDiabetesMedicationId', chosenId)
                                }
                            />
                        </div>
                        <div className={form.formColumn}>
                            <SingleSelect
                                id="currentlyTakingBloodPressureLoweringMedicationId"
                                label="Currently Taking Blood Pressure Lowering Medication"
                                placeholder="Select currently taking blood pressure lowering medication..."
                                disabled={isDisabled}
                                menuItems={
                                    currentlyTakingBloodPressureLoweringMedicationTypes || []
                                }
                                selectedId={
                                    newEntry.currentlyTakingBloodPressureLoweringMedicationId
                                }
                                selected={getObjFromId(
                                    currentlyTakingBloodPressureLoweringMedicationTypes,
                                    newEntry.currentlyTakingBloodPressureLoweringMedicationId
                                )}
                                isAuditable={isAuditable}
                                onSearchAuditItems={onSearchAuditItems}
                                onChange={(chosenId) =>
                                    onUpdate(
                                        'currentlyTakingBloodPressureLoweringMedicationId',
                                        chosenId
                                    )
                                }
                            />
                            <SingleSelect
                                id="currentlyTakingMedicationId"
                                label="Currently Taking Medication"
                                placeholder="Select currently taking medication..."
                                disabled={isDisabled}
                                menuItems={currentlyTakingMedicationDetails || []}
                                selectedId={newEntry.currentlyTakingMedicationId}
                                selected={getObjFromId(
                                    currentlyTakingMedicationDetails,
                                    newEntry.currentlyTakingMedicationId
                                )}
                                isAuditable={isAuditable}
                                onSearchAuditItems={onSearchAuditItems}
                                onChange={(chosenId) =>
                                    onUpdate('currentlyTakingMedicationId', chosenId)
                                }
                            />
                        </div>
                    </div>

                    <Button
                        id="t2drUpdateButton"
                        disabled={isDisabled || isUpdateButtonDisabled}
                        content="Update T2DR"
                        error={errors.button}
                        clearError={() => clearError('button')}
                    />
                </form>
            </div>
            {showAuditModal && (
                <div id="auditT2DRModal">
                    <AuditT2DRModal
                        isOpen={showAuditModal}
                        label={auditLabel}
                        onClose={onAuditModalClose}
                    />
                </div>
            )}
        </div>
    );
};

export default T2DR;
